import { JamEventDetails } from '@/src/types/JamEventDetails';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { MILLIS_PER_SECOND, SECONDS_PER_DAY, SECONDS_PER_HOUR } from '../constants/DateTimeConstants';
import { SECONDS_PER_MINUTE } from '../constants/shared/time';
import { NullableNumber } from '../types/common';
import { roundFloat } from './number.utils';
import { isEmpty } from './string.utils';

export const getDuration = (seconds: NullableNumber, includeSeconds = true) => {
  if (seconds) {
    const days = Math.floor(seconds / SECONDS_PER_DAY);
    seconds -= days * SECONDS_PER_DAY;
    const hours = Math.floor(seconds / SECONDS_PER_HOUR);
    seconds -= hours * SECONDS_PER_HOUR;
    let minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
    seconds -= minutes * SECONDS_PER_MINUTE;
    if (!includeSeconds && seconds >= 30) {
      minutes += 1;
    }
    let duration = '';
    duration += days ? `${days}d ` : '';
    duration += hours ? `${hours}h ` : '';
    duration += minutes ? `${minutes}m ` : '';
    if (includeSeconds) {
      duration += seconds ? `${seconds}s` : '';
    }
    return duration;
  } else {
    return null;
  }
};

export const millisecondsToSeconds = (milliseconds: number) => {
  return milliseconds / MILLIS_PER_SECOND;
};

export const getDisplayDuration = (milliseconds: number) => {
  return getDuration(roundFloat(millisecondsToSeconds(milliseconds), 0));
};

export const timeAgo = (value: string | number) => {
  const d = typeof value === 'string' ? new Date(value) : new Date(value);
  const now = new Date();
  const seconds = Math.round(Math.abs((now.getTime() - d.getTime()) / 1000));
  const minutes = Math.round(Math.abs(seconds / 60));
  const hours = Math.round(Math.abs(minutes / 60));
  const days = Math.round(Math.abs(hours / 24));
  const months = Math.round(Math.abs(days / 30.416));
  const years = Math.round(Math.abs(days / 365));
  if (Number.isNaN(seconds)) {
    return '';
  } else if (seconds <= 45) {
    return 'a few seconds ago';
  } else if (seconds <= 90) {
    return 'a minute ago';
  } else if (minutes <= 45) {
    return `${minutes} minutes ago`;
  } else if (minutes <= 90) {
    return 'an hour ago';
  } else if (hours <= 22) {
    return `${hours} hours ago`;
  } else if (hours <= 36) {
    return 'a day ago';
  } else if (days <= 25) {
    return `${days} days ago`;
  } else if (days <= 45) {
    return 'a month ago';
  } else if (days <= 345) {
    return `${months} months ago`;
  } else if (days <= 545) {
    return 'a year ago';
  } else {
    // (days > 545)
    return `${years} years ago`;
  }
};

export const timeUntil = (value: string | number) => {
  const d = typeof value === 'string' ? new Date(value) : new Date(value);
  const now = new Date();
  const seconds = Math.round(Math.abs((now.getTime() - d.getTime()) / 1000));
  const minutes = Math.round(Math.abs(seconds / 60));
  const hours = Math.round(Math.abs(minutes / 60));
  const days = Math.round(Math.abs(hours / 24));
  const months = Math.round(Math.abs(days / 30.416));
  const years = Math.round(Math.abs(days / 365));
  if (Number.isNaN(seconds)) {
    return '';
  } else if (seconds <= 45) {
    return 'a few seconds from now';
  } else if (seconds <= 90) {
    return 'a minute from now';
  } else if (minutes <= 45) {
    return `${minutes} minutes from now`;
  } else if (minutes <= 90) {
    return 'an hour from now';
  } else if (hours <= 22) {
    return `${hours} hours from now`;
  } else if (hours <= 36) {
    return 'a day from now';
  } else if (days <= 25) {
    return `${days} days from now`;
  } else if (days <= 45) {
    return 'a month from now';
  } else if (days <= 345) {
    return `${months} months from now`;
  } else if (days <= 545) {
    return 'a year from now';
  } else {
    // (days > 545)
    return `${years} years from now`;
  }
};

/**
 * Returns true if the provided date string is in a valid format, otherwise false.
 * Valid formats are documented at https://momentjs.com/docs/#/parsing/string/
 */
export const isValidDateString = (dateString: string | null) => {
  if (isEmpty(dateString)) {
    return false;
  }
  return moment(dateString).isValid();
};

/**
 * Returns date in dd Mon YYYY format. For e.g. 01 Oct 2022
 *
 * @param date
 */
export const getFormattedDate = (date: Date, languageCode = 'en-gb') =>
  date.toLocaleDateString(languageCode, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export const getLocaleTimelineFormattedDate = (date: Date, languageCode = 'en-gb') =>
  date.toLocaleDateString(languageCode, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

export const getTimelineFormattedDate = (date: Date) => moment(date).format('MM/DD/YYYY hh:mm A');
export const getRevisionFormattedDate = (date: Date) => moment(date).format('LLL');

/**
 *
 * @param date string | null
 * @returns boolean
 *
 * check if the given string is valid date
 * and it is in ISO format.
 */
export const isValidISODate = (date: string | null) =>
  isEmpty(date) ? false : moment(date, moment.ISO_8601).isValid();

/**
 *
 * @param date string | null
 * @param format valid date format (optional)
 * @returns string formatted date string
 *
 * it verifies the string to be valid
 * ISO date and returns string in
 * valid date format
 */
export const getValidatedFormattedDate = (date: string | null, format = 'MM/DD/YYYY') => {
  if (isValidISODate(date)) {
    return moment(date).format(format);
  }
  return date;
};

/**
 * Calculates event duration from start and end dates
 * Returns duration broken down into days, hours, and minutes
 *
 * @param startDate - Event start date (string or Date)
 * @param endDate - Event end date (string or Date)
 * @returns Object with duration components or null if invalid dates
 */
export const getEventDuration = (startDate: string | Date | null, endDate: string | Date | null) => {
  if (!startDate || !endDate) {
    return null;
  }

  const start = moment(startDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const totalMinutes = end.diff(start, 'minutes');
  const totalHours = Math.floor(totalMinutes / 60);

  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  return {
    totalMinutes,
    totalHours,
    days,
    hours,
    minutes,
  };
};

/**
 * Formats event duration into a human-readable string using i18n
 *
 * @param t - Translation function
 * @param event - Event details containing start and end dates
 * @returns Formatted duration string or fallback message
 */
export const formatEventDuration = (t: TFunction, event: JamEventDetails): string => {
  const duration = getEventDuration(event.eventStartDate, event.eventEndDate);

  if (!duration) {
    return t(i18nKeys.events.rules.durationNotSpecified);
  }

  const { days, hours, minutes } = duration;
  const timeParts: string[] = [];

  if (days > 0) {
    const key = days === 1 ? i18nKeys.time.formatted.day : i18nKeys.time.formatted.days;
    timeParts.push(t(key, { amount: days }));
  }

  if (hours > 0) {
    const key = hours === 1 ? i18nKeys.time.formatted.hour : i18nKeys.time.formatted.hours;
    timeParts.push(t(key, { amount: hours }));
  }

  if (minutes > 0) {
    const key = minutes === 1 ? i18nKeys.time.formatted.minute : i18nKeys.time.formatted.minutes;
    timeParts.push(t(key, { amount: minutes }));
  }

  return timeParts.length > 0 ? timeParts.join(' ') : t(i18nKeys.events.rules.durationNotSpecified);
};
